import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import logger from '../utils/logger';

interface LanguageIconProps {
  onClick: () => void;
}

export const LanguageIcon: React.FC<LanguageIconProps> = ({ onClick }) => {
  const { i18n } = useTranslation();
  const reduxLanguage = useSelector((state: RootState) => state.preferences.language);
  
  // Get the actual language code (without region)
  const getLanguageCode = (lang: string): string => {
    // Handle cases like 'en-US', 'de-DE', etc.
    return lang.split('-')[0];
  };
  
  // Use Redux language if available, otherwise fall back to i18n language
  // Make sure we're using just the language code without region
  const currentLanguage = getLanguageCode(reduxLanguage || i18n.language);
  
  // Debug logs
  useEffect(() => {
    logger.debug('LanguageIcon - Redux language:', reduxLanguage);
    logger.debug('LanguageIcon - i18n language:', i18n.language);
    logger.debug('LanguageIcon - currentLanguage displayed:', currentLanguage);
  }, [reduxLanguage, i18n.language, currentLanguage]);

  return (
    <button
      onClick={onClick}
      className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      aria-label="Change language"
    >
      <div className="flex items-center space-x-1">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-gray-600 dark:text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
          {currentLanguage.toUpperCase()}
        </span>
      </div>
    </button>
  );
}; 